<template>
  <ModalSmall :title="isEditMode ? t('label_edit_user') : t('label_add_user')" @close="closeModal">
    <div class="container">
      <div class="body">
        <p class="title">{{ t('label_basic_info') }}</p>
        <UserManageAddFormBasic v-model="userInfoNew" :is-edit-mode="isEditMode" />
      </div>

      <div class="footer">
        <div class="button-wrapper">
          <ButtonProcess
            v-if="isEditMode"
            :secondary="true"
            :disabled="false"
            :class="{ disabled: false }"
            :loading="loadingForDelete"
            @click="removeEmployee"
          >
            {{ t('button_delete') }}
          </ButtonProcess>
          <ButtonProcess
            :disabled="false"
            :class="{ disabled: false }"
            :loading="loadingForCreateOrUpdate"
            @click="onclickCreateEmployee"
          >
            {{ isEditMode ? t('label_edit_user') : t('label_add_user') }}
          </ButtonProcess>
        </div>
      </div>
    </div>
  </ModalSmall>
</template>

<script setup lang="ts">
import { cloneDeep, isEmpty } from 'lodash';

import { createEmployee, deleteEmployee, fetchPublicKey, updateEmployee } from '@/api/apiService';
import UserManageAddFormBasic from '@/components/domains/self-service/user-manage/UserManageAddFormBasic.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { getBrowserLanguage } from '@/static/languages';
import { useAlertStore } from '@/stores/alert';
import { useToastStore } from '@/stores/toast.js';
import { IUserInfo } from '@/types/components';
import { IResponsePartnerEmployee } from '@/types/response';
import { encryptPassword } from '@/utils/encryptor';
import {
  validateId,
  validateLanguage,
  validateName,
  validatePassword,
  validatePasswordConfirm,
} from '@/utils/validators';

const props = withDefaults(
  defineProps<{
    userInfo: IResponsePartnerEmployee;
  }>(),
  {
    userInfo: () => ({}) as IResponsePartnerEmployee,
  }
);

const emit = defineEmits<{ close: []; refresh: [] }>();

const { t } = useI18n();

const toastStore = useToastStore();
const alertStore = useAlertStore();

const loadingForCreateOrUpdate = ref(false);
const loadingForDelete = ref(false);
const userInfoNew = ref<IUserInfo>({
  loginId: '',
  nickname: '',
  language: getBrowserLanguage(),
  groupIds: [],
  password: '',
  passwordConfirm: '',
});

const isEditMode = computed<boolean>(() => {
  return !isEmpty(props.userInfo);
});

const closeModal = () => {
  emit('close');
};

const validate = () => {
  const { loginId, nickname, language, password: pw, passwordConfirm: pwConfirm } = userInfoNew.value;

  // 아이디 유효성 검사
  const idError = validateId(loginId);
  if (idError) {
    toastStore.error(t(idError));
    return false;
  }

  // 이름 유효성 검사
  const nameError = validateName(nickname);
  if (nameError) {
    toastStore.error(t(nameError));
    return false;
  }

  // 언어 검사
  const LanguageError = validateLanguage(language);
  if (LanguageError) {
    toastStore.error(t(LanguageError));
    return false;
  }

  if (!isEditMode.value || pw || pwConfirm) {
    // 비밀번호 유효성 검사
    const passwordError = validatePassword(pw);
    if (passwordError) {
      toastStore.error(t(passwordError));
      return false;
    }

    // 비밀번호 재확인 유효성 검사
    const passwordConfirmError = validatePasswordConfirm(pw, pwConfirm);
    if (passwordConfirmError) {
      toastStore.error(t(passwordConfirmError));
      return false;
    }
  }

  return true;
};

const getEncryptedPassword = async (password: string) => {
  const { data: publicKeyData } = await fetchPublicKey();
  return await encryptPassword(publicKeyData.partnerPublicKey, password);
};

const onclickCreateEmployee = async () => {
  if (!validate()) return;
  loadingForCreateOrUpdate.value = true;

  const { nickname, language, loginId: userId, password: pw } = userInfoNew.value;
  let password = '';

  const param = {
    ...(isEditMode.value ? {} : { userId }),
    nickname,
    username: nickname,
    languageCode: language,
    groupIds: [],
    sampleRate: '16000',
  };

  if (pw) {
    password = await getEncryptedPassword(pw);
  }

  if (isEditMode.value) {
    //Update
    const response = await updateEmployee(userInfoNew.value.userId, password, {
      body: JSON.stringify(param),
      voice: '',
    });
    if (response.success) {
      toastStore.info(t('alert_update_success'));
      emit('refresh');
      closeModal();
    }
  } else {
    const response = await createEmployee({
      ...param,
      password,
    });

    if (response.success) {
      toastStore.info(t('alert_create_success'));
      emit('refresh');
      closeModal();
    } else if (response.errorCode === 1018) {
      toastStore.error(t('alert_duplicate_id'));
    }
  }
  loadingForCreateOrUpdate.value = false;
};

const removeEmployee = () => {
  alertStore.confirm(
    {
      title: t('guide'),
      message: t('alert_confirm_delete'),
      subMessage: t('alert_delete_irreversible'),
    },
    {
      confirm: async () => {
        loadingForDelete.value = true;
        const response = await deleteEmployee(props.userInfo.userId);
        if (response.success) {
          toastStore.info(t('alert_delete_success'));
          emit('refresh');
          closeModal();
        }
        loadingForDelete.value = false;
      },
    }
  );
};

onMounted(() => {
  if (isEditMode.value) {
    userInfoNew.value = cloneDeep({
      ...props.userInfo,
      language: props.userInfo.languageCode,
    });
  }
});
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  max-width: 75rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.body {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  .title {
    text-align: center;
    width: 100%;
    font-weight: 500;
  }
}

.footer {
  width: 100%;
  padding-top: 2rem;
}

.button-wrapper {
  width: 100%;
  display: flex;
  gap: $size-s;
}

.eye-icon {
  position: absolute;
  right: 0.5rem;
  bottom: 0;
  transform: translateY(-50%);
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
}

.wrapper-checkbox {
  display: flex;
  gap: 2rem;
  padding-left: 0.5rem;
}

.group-name-wrap {
  display: flex;
  gap: 0.5rem;
}

.plus-icon {
  min-width: 1.25rem;
  max-width: 1.25rem;
  min-height: 1.25rem;
  fill: var(--color-gray-900);
}
</style>
